import { useCallback, useEffect, useRef, useState } from "react";

import { apiErrorMessage } from "./useAccountPlan";
import { useAppDispatch } from "../Store/Hooks";
import {
  askPlanChatThunk,
  createPlanChatThunk,
  fetchPlanChatMessagesThunk,
  fetchPlanChatsThunk,
  fetchPlanChatSuggestionsThunk,
  planChatFeedbackThunk,
} from "../Store/Reducers/AccountPlanSlice";
import type {
  ChatContext,
  ChatMessage,
  ChatSuggestions,
  PlanChat,
} from "../Types/AccountPlan";

export type ChatErrorKind =
  | "rate_limited"
  | "llm_failed"
  | "no_plan"
  /** 409 not_confirmed: no version is confirmed yet (decision D6, Phase 2). */
  | "not_confirmed"
  | "other";

export interface AskInput {
  question: string;
  /** Set only when the question IS a standard briefing question (stored answer). */
  questionId?: string | null;
  context?: ChatContext | null;
}

export interface ChatError {
  kind: ChatErrorKind;
  message: string;
  /** What to resend on Retry. */
  retry?: AskInput;
}

/** A message in the panel: a user question can be marked failed until it's retried. */
export type PanelMessage = ChatMessage & { failed?: boolean };

const ERROR_TEXT: Record<Exclude<ChatErrorKind, "other">, string> = {
  rate_limited:
    "You've asked a lot of questions in the last hour. Try again in a little while.",
  llm_failed:
    "The answer couldn't be generated. Your question is saved — try again.",
  no_plan: "There's no processed plan version to ask about yet.",
  not_confirmed:
    "Ask about this plan is available once the AE confirms the plan.",
};

/** HTTP status + code from an ApiRequest error (its message is a JSON string). */
export const chatErrorOf = (err: unknown): ChatError => {
  let status: number | undefined;
  let code: string | undefined;
  try {
    const parsed = JSON.parse((err as Error)?.message || "");
    status = parsed?.status;
    code =
      parsed?.code ||
      parsed?.content?.code ||
      (typeof parsed?.message === "object" ? parsed.message?.code : undefined);
  } catch {
    // not an HTTP error
  }
  const kind: ChatErrorKind =
    code === "rate_limited" || status === 429
      ? "rate_limited"
      : code === "llm_failed" || status === 503
        ? "llm_failed"
        : code === "not_confirmed"
          ? "not_confirmed"
          : code === "no_plan" || status === 409
            ? "no_plan"
            : "other";
  return {
    kind,
    message:
      kind === "other"
        ? apiErrorMessage(err, "Something went wrong. Try again.")
        : ERROR_TEXT[kind],
  };
};

const tempQuestion = (input: AskInput): PanelMessage => ({
  id: `temp-${Date.now()}`,
  role: "user",
  content: input.question,
  claims: [],
  follow_ups: [],
  not_in_plan: false,
  source: null,
  question_id: input.questionId ?? null,
  context: input.context ?? null,
  nearby: [],
  held_back: 0,
  feedback: null,
  created_at: new Date().toISOString(),
});

/**
 * The plan chat for one account (Option A): suggestions, my chats, the open
 * chat's messages, ask (optimistic question, then the server's pair) and
 * feedback. A chat is created lazily on the first question, bound to `fileId`.
 * Requests go through AccountPlanSlice's thunks; the panel's state stays here.
 */
export const useAccountPlanChat = (
  accountId: string,
  fileId: string | null,
  open: boolean
) => {
  const [suggestions, setSuggestions] = useState<ChatSuggestions | null>(null);
  const [chats, setChats] = useState<PlanChat[]>([]);
  const [chat, setChat] = useState<PlanChat | null>(null);
  const [messages, setMessages] = useState<PanelMessage[]>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  const dispatch = useAppDispatch();
  const chatRef = useRef<PlanChat | null>(null);
  const pendingRef = useRef(false);
  chatRef.current = chat;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    dispatch(fetchPlanChatSuggestionsThunk({ accountId, fileId }))
      .unwrap()
      .then(res => !cancelled && setSuggestions(res))
      .catch(() => !cancelled && setSuggestions(null));
    dispatch(fetchPlanChatsThunk({ accountId }))
      .unwrap()
      .then(res => !cancelled && setChats(res))
      .catch(() => !cancelled && setChats([]));
    return () => {
      cancelled = true;
    };
  }, [accountId, dispatch, fileId, open]);

  const newChat = useCallback(() => {
    setChat(null);
    setMessages([]);
    setError(null);
  }, []);

  const openChat = useCallback(
    async (target: PlanChat) => {
      setChat(target);
      setError(null);
      setLoading(true);
      try {
        setMessages(
          await dispatch(
            fetchPlanChatMessagesThunk({ accountId, chatId: target.id })
          ).unwrap()
        );
      } catch (err) {
        setMessages([]);
        setError(chatErrorOf(err));
      } finally {
        setLoading(false);
      }
    },
    [accountId, dispatch]
  );

  const ask = useCallback(
    async (input: AskInput) => {
      const question = input.question.trim();
      if (!question || pendingRef.current) return;
      const temp = tempQuestion({ ...input, question });
      pendingRef.current = true;
      setPending(true);
      setError(null);
      setMessages(m => [...m.filter(x => !x.failed), temp]);
      try {
        let current = chatRef.current;
        if (!current) {
          const created = await dispatch(
            createPlanChatThunk({ accountId, fileId })
          ).unwrap();
          current = created;
          chatRef.current = created;
          setChat(created);
          setChats(cs => [created, ...cs]);
        }
        const res = await dispatch(
          askPlanChatThunk({
            accountId,
            chatId: current.id,
            question,
            questionId: input.questionId,
            context: input.context,
          })
        ).unwrap();
        setMessages(m => [
          ...m.filter(x => x.id !== temp.id),
          res.question,
          res.answer,
        ]);
      } catch (err) {
        setMessages(m =>
          m.map(x => (x.id === temp.id ? { ...x, failed: true } : x))
        );
        setError({ ...chatErrorOf(err), retry: { ...input, question } });
      } finally {
        pendingRef.current = false;
        setPending(false);
      }
    },
    [accountId, dispatch, fileId]
  );

  const retry = useCallback(() => {
    if (error?.retry) void ask(error.retry);
  }, [ask, error]);

  const feedback = useCallback(
    async (messageId: string, rating: 1 | -1) => {
      const current = chatRef.current;
      if (!current) return;
      // Kept so a failed save puts back the rating the user had, not "none".
      let previous: ChatMessage["feedback"] = null;
      setMessages(m =>
        m.map(x => {
          if (x.id !== messageId) return x;
          previous = x.feedback;
          return { ...x, feedback: rating };
        })
      );
      try {
        const updated = await dispatch(
          planChatFeedbackThunk({
            accountId,
            chatId: current.id,
            messageId,
            rating,
          })
        ).unwrap();
        if (updated?.id)
          setMessages(m => m.map(x => (x.id === messageId ? updated : x)));
      } catch {
        setMessages(m =>
          m.map(x => (x.id === messageId ? { ...x, feedback: previous } : x))
        );
      }
    },
    [accountId, dispatch]
  );

  return {
    suggestions,
    chats,
    chat,
    messages,
    pending,
    loading,
    error,
    ask,
    retry,
    newChat,
    openChat,
    feedback,
  };
};
